import React, {useEffect, useState} from 'react';
import {Alert, Box, Button, Chip, CircularProgress, Divider, Link, Stack, Typography} from '@mui/material';
import PaymentOutlinedIcon from '@mui/icons-material/PaymentOutlined';
import AddLinkOutlinedIcon from '@mui/icons-material/AddLinkOutlined';
import LinkOffOutlinedIcon from '@mui/icons-material/LinkOffOutlined';
import {useConfirm} from 'material-ui-confirm';
import {
    useCreateStripeAccountLinkMutation,
    useCreateStripeOnboardingLinkMutation,
    useDisconnectStripeAccountMutation,
    useGetStripeConnectionStatusQuery
} from '../../../../api/services/paymentsService';
import useNotification from '../../../../utility/hooks/useNotification';

const RETURN_MESSAGES = {
    linked: {
        severity: 'success',
        text: 'Your Stripe account is linked.'
    },
    connected: {
        severity: 'success',
        text: 'Back from Stripe. Checking whether setup is complete.'
    },
    refresh: {
        severity: 'warning',
        text: 'Stripe setup was not finished. Choose Create a new Stripe account to carry on where you left off.'
    },
    link_refused: {
        severity: 'warning',
        text: 'Stripe was not given permission, so no account has been linked.'
    },
    link_failed: {
        severity: 'error',
        text: 'Stripe could not complete the link. Please try again.'
    }
};

const PaymentsSettings = () => {
    const {
        data: status,
        isLoading,
        isFetching,
        refetch
    } = useGetStripeConnectionStatusQuery();
    const [createOnboardingLink, {isLoading: isCreatingAccount}] = useCreateStripeOnboardingLinkMutation();
    const [createAccountLink, {isLoading: isLinking}] = useCreateStripeAccountLinkMutation();
    const [disconnect, {isLoading: isDisconnecting}] = useDisconnectStripeAccountMutation();
    const {showErrorNotification, showSuccessNotification} = useNotification();
    const confirm = useConfirm();
    const [returnMessage, setReturnMessage] = useState(null);

    useEffect(() => {
        const url = new URL(window.location.href);
        const marker = url.searchParams.get('stripe');

        if (!marker) return;

        if (RETURN_MESSAGES[marker]) {
            setReturnMessage(RETURN_MESSAGES[marker]);
        }

        url.searchParams.delete('stripe');
        window.history.replaceState({}, '', url.toString());
        refetch();
    }, [refetch]);

    const openStripe = async (start, failureMessage) => {
        try {
            const url = await start().unwrap();

            if (!url) {
                showErrorNotification('Stripe did not return a setup link. Please try again.');
                return;
            }

            window.location.assign(url);
        } catch (e) {
            showErrorNotification(e?.data?.message ?? failureMessage);
        }
    };

    const handleCreateAccount = () =>
        openStripe(createOnboardingLink, 'Could not start Stripe setup. Please try again.');

    const handleLinkAccount = () =>
        openStripe(createAccountLink, 'Could not start linking your Stripe account. Please try again.');

    const handleDisconnect = async () => {
        try {
            await confirm({
                title: 'Stop taking card payments?',
                description: 'Forms will no longer be able to collect payments. Your Stripe account and its ' +
                    'payment history are not affected, and you can connect again at any time.',
                confirmationText: 'Disconnect',
                cancellationText: 'Cancel'
            });
        } catch {
            return;
        }

        try {
            await disconnect().unwrap();
            showSuccessNotification('Stripe disconnected');
            setReturnMessage(null);
            refetch();
        } catch (e) {
            showErrorNotification(e?.data?.message ?? 'Could not disconnect Stripe. Please try again.');
        }
    };

    if (isLoading) {
        return (
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                p: 4
            }}>
                <CircularProgress/>
            </Box>
        );
    }

    const configured = status?.configured === true;
    const linkingAvailable = status?.linkingAvailable === true;
    const connected = status?.connected === true;
    const setupUnfinished = status?.setupUnfinished === true;
    const chargesEnabled = status?.chargesEnabled === true;
    const linkedTheirOwnAccount = status?.connectedVia === 'OAuth';
    const isBusy = isCreatingAccount || isLinking || isFetching;

    return (
        <Box sx={{
            maxWidth: 760,
            py: 3
        }}>
            <Typography variant="h6" sx={{mb: 1}}>Card payments</Typography>
            <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
                Connect your school&apos;s own Stripe account to collect payments on forms. Payments go directly
                to your account &mdash; Admicity never holds your money and takes no fee. Your school creates
                and owns the Stripe account; link one you already have, or create one with Stripe as part of
                setting this up.
            </Typography>

            {returnMessage && (
                <Alert severity={returnMessage.severity} sx={{mb: 3}} onClose={() => setReturnMessage(null)}>
                    {returnMessage.text}
                </Alert>
            )}

            {!configured && (
                <Alert severity="info" sx={{mb: 3}}>
                    Card payments are not available on this environment yet. Contact Admicity support.
                </Alert>
            )}

            <Divider sx={{mb: 3}}/>

            <Stack direction="row" alignItems="center" spacing={2} sx={{mb: 2}}>
                <Typography variant="subtitle1" sx={{fontWeight: 500}}>Stripe</Typography>
                {connected && chargesEnabled && <Chip size="small" color="success" label="Ready to take payments"/>}
                {connected && !chargesEnabled && <Chip size="small" color="warning" label="Awaiting Stripe"/>}
                {!connected && <Chip size="small" label="Not connected"/>}
            </Stack>

            {!connected && setupUnfinished && (
                <Alert severity="info" sx={{mb: 2}}>
                    Stripe setup was started but not finished, so no account is connected and forms cannot take
                    payments yet. Creating a new Stripe account will pick up where you left off.
                </Alert>
            )}

            {connected && !chargesEnabled && (
                <Alert severity="warning" sx={{mb: 2}}>
                    Stripe has everything it needs and is reviewing this account. Card payments will start
                    working once Stripe activates them, and there is nothing further for you to do.
                </Alert>
            )}

            {connected && status?.accountId && (
                <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                    Connected account: <code>{status.accountId}</code>
                </Typography>
            )}

            <Stack direction="row" spacing={2} sx={{mt: 2}} flexWrap="wrap" useFlexGap>
                {!connected && (
                    <>
                        <Button
                            variant="contained"
                            startIcon={<AddLinkOutlinedIcon/>}
                            onClick={handleLinkAccount}
                            disabled={!configured || !linkingAvailable || isBusy}
                        >
                            {isLinking ? 'Opening Stripe…' : 'Connect an existing Stripe account'}
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<PaymentOutlinedIcon/>}
                            onClick={handleCreateAccount}
                            disabled={!configured || isBusy}
                        >
                            {isCreatingAccount
                                ? 'Opening Stripe…'
                                : setupUnfinished
                                    ? 'Continue creating a Stripe account'
                                    : 'Create a new Stripe account'}
                        </Button>
                    </>
                )}

                {connected && !linkedTheirOwnAccount && (
                    <Button
                        variant="contained"
                        startIcon={<PaymentOutlinedIcon/>}
                        onClick={handleCreateAccount}
                        disabled={!configured || isBusy}
                    >
                        {isCreatingAccount ? 'Opening Stripe…' : 'Manage on Stripe'}
                    </Button>
                )}

                {connected && (
                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={<LinkOffOutlinedIcon/>}
                        onClick={handleDisconnect}
                        disabled={isDisconnecting}
                    >
                        Disconnect
                    </Button>
                )}
            </Stack>

            {!connected && !linkingAvailable && configured && (
                <Alert severity="info" sx={{mt: 2}}>
                    Linking a Stripe account you already have is not available on this environment yet. Contact
                    Admicity support.
                </Alert>
            )}

            {connected && linkedTheirOwnAccount && (
                <Typography variant="body2" color="text.secondary" sx={{mt: 2}}>
                    This account is managed in your own Stripe dashboard.
                </Typography>
            )}

            <Typography variant="caption" color="text.secondary" sx={{
                display: 'block',
                mt: 3
            }}>
                Set the amount for each payment on the form itself, using a Payment question in the form
                builder. See <Link href="https://stripe.com/gb/legal/ssa" target="_blank"
                                   rel="noopener noreferrer">Stripe&apos;s terms</Link> for the account you
                connect.
            </Typography>
        </Box>
    );
};

export default PaymentsSettings;
